"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Trophy, Sparkles, Crosshair, Building2, Hammer, Target } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const GOALS = [
  { icon: Trophy, title: "Establish Operational Excellence as a Discipline", body: "To move excellence from the poster on the wall to the system in the engine room — making it repeatable, accountable, and verifiable across organizations, governments, and institutions.", progress: 100, gradient: "from-red/15", accent: "text-red" },
  { icon: Sparkles, title: "Advance Women in Leadership Through Evidence", body: "To prove, through The 70% Project and a decade of operating data, that the talent the world overlooks is the talent that outperforms — shifting the conversation from morality to operations.", progress: 100, gradient: "from-amber-gold/15", accent: "text-amber-gold" },
  { icon: Crosshair, title: "Bridge Medicine, Technology, and Politics", body: "To demonstrate that clinical rigor, applied to technological systems under political-grade stakes, produces outcomes no single-domain specialist can achieve — and to make this method teachable.", progress: 85, gradient: "from-purple/15", accent: "text-purple" },
  { icon: Building2, title: "Drive Governance Transformation", body: "To convert political authority into measurable public outcomes through digital governance, citizen engagement, and policy implementation that serves the governed.", progress: 90, gradient: "from-cyan/15", accent: "text-cyan" },
  { icon: Hammer, title: "Build Organizations That Last", body: "To construct consequential institutions through systems, not charisma — measured by outcomes, not publicity; by duration, not disruption.", progress: 95, gradient: "from-emerald/15", accent: "text-emerald" },
];

export function GoalsSection() {
  return (
    <Section id="goals" ariaLabel="Goals and Objectives" background="muted">
      <SectionHeader
        icon={Target}
        overline="Goals and Objectives"
        overlineClassName="text-red"
        pillClassName="glass-tinted-red border-red/20"
        title={
          <>
            What the Operator <span className="text-gradient-energy">Aims to Achieve.</span>
          </>
        }
        subtitle="The mission: to deliver intelligence-driven outcomes enabling governments, campaigns, enterprises, and institutions to navigate complexity with clarity, act with precision, and win with integrity."
      />

      <div className="space-y-4">
        {GOALS.map((g, i) => (
          <motion.div
            key={g.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className={`group glass-card rounded-2xl p-6 bg-gradient-to-r ${g.gradient} to-transparent border border-border/30 hover:scale-[1.005] transition-all`}
          >
            <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
              <div className="flex items-center gap-3">
                <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>
                  <g.icon className="size-6 text-white" />
                </div>
              </div>
              <div>
                <h3 className={`text-heading-md mb-2 ${g.accent}`}>{g.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{g.body}</p>
              </div>
              <div className="text-right shrink-0">
                <div className={`text-display-sm font-bold ${g.accent} leading-none mb-1.5`}>{g.progress}%</div>
                <div className="text-caption text-muted-foreground font-semibold uppercase">Progress</div>
                <div className="w-24 h-1.5 rounded-full bg-muted/40 mt-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${g.progress}%` }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.2 + i * 0.1 }}
                    className="h-full bg-gradient-sovereign"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
